/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import { useAuth } from "hooks/useAuth";
import apiClient from "Api"
import { useEffect, useState } from "react";
import Loading from "components/Loading";
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { useSnackbar } from 'notistack';
import { date } from "yup";

function Dashboard() {
  const [isLoading, setIsLoading] = useState(false);
  const { sales, tasks } = reportsLineChartData;
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [hasPendingAppConfiguration, setHasPendingAppConfiguration] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    daySales: 0,
    totalDaySales: 0,
    monthSales: 0,
    totalMonthSales: 0,
    monthPendingSales: 0,
    totalMonthPendingSales: 0,
    monthConfirmedSales: 0,
    totalMonthConfirmedSales: 0,
    dayGroupSales: {},
    monthGroupSales: {},
    conversionPix: null,
    conversionBoleto: null,
    conversionCreditCard: null
  })

  const dateTime = new Date().toLocaleString();

  function loadData() {
    setIsLoading(true);
    apiClient.get(`/account/currentuser`).then(responseCurrentUser => {
      setHasPendingAppConfiguration(responseCurrentUser.data.hasPendingAppConfiguration);

      apiClient.get(`/tenants/dashboard`).then(responseDashboard => {
        setDashboardData(responseDashboard.data);
        setIsLoading(false);
      }).catch(e => {
        setIsLoading(false);
        enqueueSnackbar('Erro ao retornar dados do dashboard!', { variant: "error" });
      });
    }).catch(e => {
      setIsLoading(false);
      enqueueSnackbar('Erro ao retornar dados do usuário!', { variant: "error" });
    });
  }

  useEffect(() => {
    loadData();
  }, []);

  return (
    <DashboardLayout>
      <Loading isLoading={isLoading}></Loading>
      <DashboardNavbar />
      {!isLoading &&
        <MDBox py={3}>
          <Grid container spacing={3}>
            {hasPendingAppConfiguration &&
              <Grid item xs={12} md={12} mb={2}>
                <Alert severity="warning" variant="outlined">
                  <Link href="/settings" underline="always">Clique aqui para finalizar as configurações e ativar o app.</Link>
                </Alert>
              </Grid>}
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="primary"
                  icon="today"
                  title="Vendas no dia"
                  count={dashboardData.daySales}
                  percentage={{ color: "success", amount: dashboardData.totalDaySales?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }), label: "Total" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  icon="calendar_month"
                  title="Vendas no mês"
                  count={dashboardData.monthSales}
                  percentage={{ color: "success", amount: dashboardData.totalMonthSales?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }), label: "Total" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="warning"
                  icon="pending_actions"
                  title="Vendas pendentes no mês"
                  count={dashboardData.monthPendingSales}
                  percentage={{ color: "success", amount: dashboardData.totalMonthPendingSales?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }), label: "Total" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="success"
                  icon="paid"
                  title="Vendas pagas no mês"
                  count={dashboardData.monthConfirmedSales}
                  percentage={{ color: "success", amount: dashboardData.totalMonthConfirmedSales?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }), label: "Total" }}
                />
              </MDBox>
            </Grid>
          </Grid>
          <MDBox mt={4.5}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox mb={3}>
                  <ReportsBarChart
                    color="info"
                    title="Vendas/Hora"
                    description={new Date().toLocaleDateString()}
                    date="dia"
                    chart={dashboardData.dayGroupSales}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="success"
                    title="Vendas/Dia"
                    description={new Date().toLocaleString('en-us', { month: 'short', year: 'numeric' })}
                    date="Mês"
                    chart={dashboardData.monthGroupSales}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <Grid container spacing={3}>
            {dashboardData.conversionPix != null &&
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="secondary"
                    icon="pix"
                    title="Taxa de conversão PIX (mês atual)"
                    count={dashboardData.conversionPix + "%"}
                  />
                </MDBox>
              </Grid>}
            {dashboardData.conversionBoleto != null &&
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="secondary"
                    icon="receipt"
                    title="Taxa de conversão BOLETO (mês atual)"
                    count={dashboardData.conversionBoleto + "%"}
                  />
                </MDBox>
              </Grid>}
            {dashboardData.conversionCreditCard != null &&
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="secondary"
                    icon="credit_card"
                    title="Taxa de conversão CARTÃO DE CRÉDITO (mês atual)"
                    count={dashboardData.conversionCreditCard + "%"}
                  />
                </MDBox>
              </Grid>}
          </Grid>
        </MDBox>}
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
